import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowRight,
  Calendar,
  Clock,
  Video,
  MapPin,
  FileDown,
  X,
} from 'lucide-react';
import { Button } from '@/client/components/ui/button';
import { Alert } from '@/client/components/ui/alert';
import { EmptyState } from '@/client/components/ui/empty-state';
import { api } from '@/client/lib/api';
import { isLocale, type Locale } from '@/client/i18n';
import { cn } from '@/client/lib/utils';

type BookingStatus =
  | 'pending_approval'
  | 'approved_awaiting_payment'
  | 'paid_confirmed'
  | 'declined'
  | 'cancelled'
  | 'expired'
  | 'completed'
  | 'no_show'
  | 'refunded';

type MyBooking = {
  id: string;
  status: BookingStatus;
  createdAt: number;
  paidAt: number | null;
  expiresAt: number | null;
  meetingUrl: string | null;
  meetingLocation: string | null;
  consultationTitle: string;
  consultationFormat: string | null;
  slotStartAt: number | null;
  slotEndAt: number | null;
  paymentToken: string | null;
  icsUrl: string | null;
};

const STATUS_LABEL_SK: Record<BookingStatus, string> = {
  pending_approval: 'Čaká na schválenie',
  approved_awaiting_payment: 'Čaká na platbu',
  paid_confirmed: 'Potvrdené',
  declined: 'Neschválené',
  cancelled: 'Zrušené',
  expired: 'Expirované',
  completed: 'Ukončené',
  no_show: 'Neúčasť',
  refunded: 'Refundované',
};

const STATUS_LABEL_CS: Record<BookingStatus, string> = {
  pending_approval: 'Čeká na schválení',
  approved_awaiting_payment: 'Čeká na platbu',
  paid_confirmed: 'Potvrzeno',
  declined: 'Neschváleno',
  cancelled: 'Zrušeno',
  expired: 'Vypršelo',
  completed: 'Ukončeno',
  no_show: 'Neúčast',
  refunded: 'Refundováno',
};

const STATUS_STYLES: Record<BookingStatus, string> = {
  pending_approval: 'bg-warning/12 border-warning/30 text-[#7a5500]',
  approved_awaiting_payment: 'bg-primary-light/30 border-primary/25 text-primary-dark',
  paid_confirmed: 'bg-success/12 border-success/25 text-success',
  declined: 'bg-white/70 border-glass-border-strong text-text-secondary',
  cancelled: 'bg-white/70 border-glass-border-strong text-text-secondary',
  expired: 'bg-white/70 border-glass-border-strong text-text-secondary',
  completed: 'bg-success/12 border-success/25 text-success',
  no_show: 'bg-error/10 border-error/25 text-error',
  refunded: 'bg-white/70 border-glass-border-strong text-text-secondary',
};

export function PortalBookings() {
  const params = useParams();
  const locale: Locale = isLocale(params.locale) ? params.locale : 'sk';
  const queryClient = useQueryClient();
  const [confirmingId, setConfirmingId] = useState<string | null>(null);

  const list = useQuery({
    queryKey: ['me', 'bookings'],
    queryFn: () => api<MyBooking[]>('/api/v1/me/bookings'),
  });

  const cancel = useMutation({
    mutationFn: (id: string) =>
      api<{ ok: boolean; wasPaid: boolean }>(`/api/v1/me/bookings/${id}/cancel`, {
        method: 'POST',
      }),
    onSuccess: (resp) => {
      setConfirmingId(null);
      queryClient.invalidateQueries({ queryKey: ['me', 'bookings'] });
      if (resp.wasPaid) {
        alert(
          locale === 'cs'
            ? 'Rezervace byla zrušena. O refundaci se ti ozveme do 14 dní podle našich pravidel.'
            : 'Rezervácia bola zrušená. O refunde sa ti ozveme do 14 dní podľa našich pravidiel.',
        );
      }
    },
  });

  if (list.isLoading) {
    return (
      <div className="space-y-4">
        {[0, 1].map((i) => (
          <div key={i} className="dr-card-static h-32 animate-shimmer" />
        ))}
      </div>
    );
  }

  if (!list.data || list.data.length === 0) {
    return (
      <EmptyState
        variant="page"
        title={locale === 'cs' ? 'Žádné rezervace' : 'Žiadne rezervácie'}
        description={
          locale === 'cs'
            ? 'Ještě jsi si nerezervovala konzultaci.'
            : 'Ešte si si nerezervovala konzultáciu.'
        }
        action={
          <Link to={`/${locale}/konzultacie`}>
            <Button>
              {locale === 'cs' ? 'Prohlédnout konzultace' : 'Prezri konzultácie'}
              <ArrowRight size={14} />
            </Button>
          </Link>
        }
      />
    );
  }

  const now = Math.floor(Date.now() / 1000);
  const statusLabels = locale === 'cs' ? STATUS_LABEL_CS : STATUS_LABEL_SK;

  return (
    <div className="space-y-4 stagger-children">
      {list.data.map((b) => {
        const slotIsPast = (b.slotEndAt ?? 0) < now;
        const cancellable =
          !slotIsPast &&
          (b.status === 'pending_approval' ||
            b.status === 'approved_awaiting_payment' ||
            b.status === 'paid_confirmed');

        const dateLabel = b.slotStartAt
          ? new Date(b.slotStartAt * 1000).toLocaleDateString(
              locale === 'cs' ? 'cs-CZ' : 'sk-SK',
              { weekday: 'long', day: 'numeric', month: 'long' },
            )
          : null;
        const timeLabel = b.slotStartAt
          ? new Date(b.slotStartAt * 1000).toLocaleTimeString(
              locale === 'cs' ? 'cs-CZ' : 'sk-SK',
              { hour: '2-digit', minute: '2-digit' },
            )
          : null;

        return (
          <article key={b.id} className="dr-card-static p-6 sm:p-7">
            <div className="flex flex-wrap items-start justify-between gap-3">
              <div className="min-w-0">
                <p className="dr-eyebrow">
                  {locale === 'cs' ? 'Konzultace' : 'Konzultácia'}
                  {b.consultationFormat ? ` · ${b.consultationFormat}` : ''}
                </p>
                <h2 className="mt-1.5 font-display text-h3 text-primary-dark">
                  {b.consultationTitle}
                </h2>
                {dateLabel && timeLabel && (
                  <div className="mt-3 flex flex-wrap items-center gap-3 font-sans text-body-small text-text-secondary">
                    <span className="inline-flex items-center gap-1.5">
                      <Calendar size={14} className="text-primary" />
                      <span className="capitalize">{dateLabel}</span>
                    </span>
                    <span className="inline-flex items-center gap-1.5">
                      <Clock size={14} className="text-primary" />
                      {timeLabel}
                    </span>
                  </div>
                )}
              </div>
              <span
                className={cn(
                  'inline-flex items-center rounded-pill border px-3 py-1 font-sans text-caption font-medium',
                  STATUS_STYLES[b.status],
                )}
              >
                {statusLabels[b.status]}
              </span>
            </div>

            {b.status === 'approved_awaiting_payment' && b.paymentToken && (
              <Alert variant="warning" className="mt-5">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <span className="font-sans text-body-small">
                    {locale === 'cs'
                      ? 'Termín je rezervovaný 48 hodin. Dokonči platbu, ať ti termín nepropadne.'
                      : 'Termín je rezervovaný 48 hodín. Dokonči platbu, aby ti termín nezhasol.'}
                  </span>
                  <Link
                    to={`/${locale}/konzultacie/platba?token=${b.paymentToken}`}
                    className="shrink-0 inline-flex items-center justify-center gap-2 rounded-pill bg-gradient-to-br from-primary to-primary-dark text-white h-9 px-4 font-sans text-body-small font-medium shadow-[0_4px_16px_rgba(117,59,189,0.28),inset_0_1px_0_rgba(255,255,255,0.18)] hover:-translate-y-px hover:shadow-[0_8px_24px_rgba(117,59,189,0.38)] transition-all"
                  >
                    {locale === 'cs' ? 'Zaplatit' : 'Zaplatiť'}
                    <ArrowRight size={14} />
                  </Link>
                </div>
              </Alert>
            )}

            {b.status === 'paid_confirmed' &&
              (b.meetingUrl || b.meetingLocation || b.icsUrl) && (
                <div className="mt-5 rounded-2xl bg-white/55 backdrop-blur-md border border-glass-border p-4 sm:p-5 space-y-2.5">
                  {b.meetingUrl && (
                    <div className="flex items-start gap-2.5 font-sans text-body-small">
                      <Video size={15} className="text-primary mt-0.5 shrink-0" />
                      <div className="min-w-0">
                        <span className="text-text-secondary">
                          {locale === 'cs' ? 'Online setkání: ' : 'Online stretnutie: '}
                        </span>
                        <a
                          href={b.meetingUrl}
                          className="text-primary hover:underline break-all"
                        >
                          {b.meetingUrl}
                        </a>
                      </div>
                    </div>
                  )}
                  {b.meetingLocation && (
                    <div className="flex items-start gap-2.5 font-sans text-body-small">
                      <MapPin size={15} className="text-primary mt-0.5 shrink-0" />
                      <div className="min-w-0">
                        <span className="text-text-secondary">
                          {locale === 'cs' ? 'Místo: ' : 'Miesto: '}
                        </span>
                        <span className="text-text-primary">{b.meetingLocation}</span>
                      </div>
                    </div>
                  )}
                  {b.icsUrl && (
                    <div>
                      <a
                        href={b.icsUrl}
                        className="inline-flex items-center gap-1.5 rounded-pill bg-white/80 border border-glass-border-strong text-primary hover:bg-primary hover:text-white hover:border-primary px-3 h-8 font-sans text-caption font-medium transition-all"
                      >
                        <FileDown size={12} />
                        {locale === 'cs' ? 'Stáhnout pozvánku (.ics)' : 'Stiahnuť pozvánku (.ics)'}
                      </a>
                    </div>
                  )}
                </div>
              )}

            {cancellable && confirmingId === b.id && (
              <div className="mt-5 rounded-2xl bg-white/70 backdrop-blur-md border border-glass-border-strong p-4 sm:p-5">
                <p className="font-sans text-body-small text-text-primary leading-relaxed">
                  {b.status === 'paid_confirmed'
                    ? locale === 'cs'
                      ? 'Po zrušení strácaš termín. Při uhrazené platbě ti refund spracujeme do 14 dní podle našich pravidel.'
                      : 'Po zrušení strácaš termín. Pri uhradenej platbe ti refund spracujeme do 14 dní podľa našich pravidiel.'
                    : locale === 'cs'
                      ? 'Naozaj chceš tuto rezervaci zrušit?'
                      : 'Naozaj chceš túto rezerváciu zrušiť?'}
                </p>
                <div className="mt-4 flex flex-wrap gap-2">
                  <Button variant="ghost" size="sm" onClick={() => setConfirmingId(null)}>
                    {locale === 'cs' ? 'Zpět' : 'Späť'}
                  </Button>
                  <Button
                    variant="danger"
                    size="sm"
                    onClick={() => cancel.mutate(b.id)}
                    disabled={cancel.isPending}
                  >
                    {cancel.isPending ? '…' : locale === 'cs' ? 'Ano, zrušit' : 'Áno, zrušiť'}
                  </Button>
                </div>
              </div>
            )}

            {cancellable && confirmingId !== b.id && (
              <div className="mt-5">
                <button
                  type="button"
                  onClick={() => setConfirmingId(b.id)}
                  className="inline-flex items-center gap-1.5 font-sans text-caption font-medium text-text-secondary hover:text-error transition-colors"
                >
                  <X size={12} />
                  {locale === 'cs' ? 'Zrušit rezervaci' : 'Zrušiť rezerváciu'}
                </button>
              </div>
            )}
          </article>
        );
      })}
    </div>
  );
}
